import { useQueries } from '@tanstack/react-query';
import { getAggregatedData } from '../historical-data';
import { useMemo } from 'react';
import { format, parseISO, eachDayOfInterval, eachMonthOfInterval, startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import type { TimePeriod } from '@/pages/map/types';

interface ChartEnergyDataParams {
  siteIds: string[];
  startDate: string;
  endDate: string;
  period: TimePeriod;
  enabled?: boolean;
}

interface DailyEnergyData {
  date: string;
  grid: number;
  solar: number;
  total: number;
}

export function useChartEnergyData({ siteIds, startDate, endDate, period, enabled = true }: ChartEnergyDataParams) {
  // Fetch energy data for all sites
  const results = useQueries({
    queries: siteIds.map(siteId => ({
      queryKey: ['chart-energy-data', siteId, startDate, endDate],
      queryFn: () => getAggregatedData({
        table_name: 'aggregated_data_1day',
        site_id: siteId,
        device_id: 'solar_subsystem',
        datapoints: ['today_grid_energy', 'today_energy'],
        start_timestamp: startDate,
        end_timestamp: endDate,
        tz: 'Asia/Bangkok'
      }),
      enabled,
      staleTime: 300000, // 5 minutes
    }))
  });

  const chartData = useMemo((): DailyEnergyData[] => {
    // If loading or no data, return empty array
    if (results.some(query => query.isLoading) || results.every(query => !query.data)) {
      return [];
    }

    // Create a map to aggregate data by date
    const dateMap = new Map<string, { grid: number; solar: number }>();

    // Process each site's data
    results.forEach(query => {
      if (!query.data?.data?.data) return;

      const siteData = query.data.data.data;
      
      // Find grid and solar data
      const gridData = siteData.find(item => item.datapoint === 'today_grid_energy');
      const solarData = siteData.find(item => item.datapoint === 'today_energy');

      // Process grid energy data
      if (gridData?.values) {
        gridData.values.forEach(dataPoint => {
          const date = dataPoint.timestamp.split('T')[0];
          const existing = dateMap.get(date) || { grid: 0, solar: 0 };
          dateMap.set(date, {
            ...existing,
            grid: existing.grid + (dataPoint.value || 0)
          });
        });
      }

      // Process solar energy data
      if (solarData?.values) {
        solarData.values.forEach(dataPoint => {
          const date = dataPoint.timestamp.split('T')[0];
          const existing = dateMap.get(date) || { grid: 0, solar: 0 };
          dateMap.set(date, {
            ...existing,
            solar: existing.solar + (dataPoint.value || 0)
          });
        });
      }
    });

    // For 'this-month', ensure all days are included
    if (period === 'this-month') {
      const start = parseISO(startDate.split('T')[0]);
      const end = parseISO(endDate.split('T')[0]);
      const allDays = eachDayOfInterval({ start, end });
      
      return allDays.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const data = dateMap.get(dateStr) || { grid: 0, solar: 0 };
        return {
          date: dateStr,
          grid: Math.round(data.grid),
          solar: Math.round(data.solar),
          total: Math.round(data.grid + data.solar)
        };
      });
    }
    
    // For 'this-year' and 'all-time', ensure all months are included
    if (period === 'this-year' || period === 'all-time') {
      // First, aggregate by month
      const monthMap = new Map<string, { grid: number; solar: number }>();
      
      // Process existing data
      dateMap.forEach((values, date) => {
        const monthKey = date.substring(0, 7); // YYYY-MM
        const existing = monthMap.get(monthKey) || { grid: 0, solar: 0 };
        monthMap.set(monthKey, {
          grid: existing.grid + values.grid,
          solar: existing.solar + values.solar
        });
      });
      
      // For 'this-year', always return all 12 months of the current year
      if (period === 'this-year') {
        const year = new Date().getFullYear();
        const allMonths = Array.from({ length: 12 }, (_, i) => {
          const monthStr = `${year}-${String(i + 1).padStart(2, '0')}`;
          const data = monthMap.get(monthStr) || { grid: 0, solar: 0 };
          return {
            date: monthStr,
            grid: Math.round(data.grid),
            solar: Math.round(data.solar),
            total: Math.round(data.grid + data.solar)
          };
        });
        return allMonths;
      }
      
      // For 'all-time', get all months in the actual data range
      const start = startOfMonth(parseISO(startDate.split('T')[0]));
      const end = endOfMonth(parseISO(endDate.split('T')[0]));
      const allMonths = eachMonthOfInterval({ start, end });
      
      // Ensure all months in the range are included
      return allMonths.map(month => {
        const monthStr = format(month, 'yyyy-MM');
        const data = monthMap.get(monthStr) || { grid: 0, solar: 0 };
        return {
          date: monthStr,
          grid: Math.round(data.grid),
          solar: Math.round(data.solar),
          total: Math.round(data.grid + data.solar)
        };
      });
    }

    // Default: return sorted data as-is
    return Array.from(dateMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, values]) => ({
        date,
        grid: Math.round(values.grid),
        solar: Math.round(values.solar),
        total: Math.round(values.grid + values.solar)
      }));
  }, [results, period]);

  const isLoading = results.some(query => query.isLoading);
  const isError = results.some(query => query.isError);

  return {
    data: chartData,
    isLoading,
    isError,
    refetch: () => Promise.all(results.map(query => query.refetch())),
  };
}